<template>
	<div id="shoppingcart-container">
		<div id="shoppingcart" :class="shoppingCartClasses" class="shoppingcart">

			<div v-if="showContent" class="shoppingcart__inner">
				<shoppingcart-header :can-create-order="isCreatePoEnabled"
				                     :delivery-dates="deliveryDates"
				                     :selected-customer-id="selectedCustomerId"
				                     :show-orders-with-availability-issues="showOrdersWithAvailabilityIssues"
				                     @on-toggle-shopping-cart="toggle"
				                     @on-toggle-create-order-modal="openPurchaseOrderModal"
				                     @toggle-orderlines-with-availability-issues="toggleOrdersWithAvailabilityIssues"
				/>

				<shoppingcart-content :delivery-dates="deliveryDates"
				                      :delivery-weeks="shoppingCartStore.orderLinesPerWeek"
				                      :show-orders-with-availability-issues="showOrdersWithAvailabilityIssues"
				                      @on-toggle-shopping-cart="toggle"
				                      @on-close-shopping-cart="closeShoppingCart"
				/>

				<shoppingcart-footer @on-toggle-shopping-cart="toggle" />
			</div>

			<shoppingcart-bar v-if="!shoppingCartExpanded"
			                  :amount-of-weeks="deliveryDatesAmount"
			                  :selected-customer-id="selectedCustomerId"
			                  @on-toggle-shopping-cart="toggle"
			/>
		</div>
	</div>

	<teleport to="#shoppingcart-modal-container">
		<alternative-pf-modal v-if="alternativePFModalOpen && currentAlternativePFGroup"
		                       :group="currentAlternativePFGroup"
		                       @close="modalStore.closeAlternativePFModal"
		                       @confirm="applyAlternativePFSelection"
		/>

		<create-purchase-order-modal v-if="createPoModalOpen"
		                             @close="closePurchaseOrderModal"
		                             @open-create-purchase-order-success-model="openCreatePurchaseOrderSuccessModal"
		                             @open-change-delivery-address-modal="openChangeDeliveryAddressModal"
		/>

		<create-purchase-order-success-modal v-if="createPoSuccessModalOpen"
		                                     :name="poName"
		                                     @close="closeCreatePurchaseOrderSuccessModal"
		/>

		<change-delivery-address-modal v-if="changeDeliveryAddressModalOpen"
		                               :delivery-addresses="deliveryAddresses"
		                               @close="closeChangeDeliveryAddressModal"
		/>
	</teleport>
</template>

<script lang="ts" setup>
	import ChangeDeliveryAddressModal from '@/components/modals/ChangeDeliveryAddressModal.vue';
	import CreatePurchaseOrderModal from '@/components/modals/CreatePurchaseOrderModal.vue';
	import CreatePurchaseOrderSuccessModal from '@/components/modals/CreatePurchaseOrderSuccessModal.vue';
	import AlternativePfModal from '@/components/modals/AlternativePFModal.vue';
	import {useModalStore} from '@/store/modal.store';
	import {
		type AlternativeForm,
		type DeliveryAddress,
		type DeliveryDate,
		getRelatedCustomersForMasterOrSalesRep,
		hasRoleMasterOrSalesRep,
		initCustomerLoggedInIntervals,
		roundUpAmount,
		shoppingCartStorage,
		shoppingCartToggle
	} from 'dummen-shared';
	import {storeToRefs} from 'pinia';
	import {computed, onBeforeMount, onMounted, ref, watch} from 'vue';
	import ShoppingcartHeader from '@/components/ShoppingcartHeader.vue';
	import ShoppingcartFooter from '@/components/ShoppingcartFooter.vue';
	import ShoppingcartContent from '@/components/ShoppingcartContent.vue';
	import ShoppingcartBar from '@/components/ShoppingcartBar.vue';
	import {useShoppingCartStore} from '@/store/shoppingcart.store.ts';
	import {updateAvailabilityFromIndex} from '@/service/availability.service.ts';
	import {useCreateOrderStore} from '@/store/create.order.store.ts';
	import {useRelatedCustomersStore} from '@/store/related.customers.store.ts';
	import {getSelectedCustomer} from 'dummen-shared/src/services/selected.customer.storage.service.ts';

	// State / Data
	const modalStore = useModalStore();
	const shoppingCartStore = useShoppingCartStore();
	const createOrderStore = useCreateOrderStore();
	const relatedCustomersStore = useRelatedCustomersStore();

	const {createPoModalOpen, createPoSuccessModalOpen, changeDeliveryAddressModalOpen, alternativePFModalOpen, currentAlternativePFGroup} = storeToRefs(modalStore);

	const shoppingCartExpanded = ref(false);
	const deliveryAddresses = ref<DeliveryAddress[]>([]);
	const poName = ref<string>('');
	const showContent = ref(false);

	// Computed
	const orderLines = computed(() => shoppingCartStorage.value.orderLines);

	const selectedCustomerId = computed(() => getSelectedCustomer());

	const shoppingCartClasses = computed(() => {
		return {
			'shoppingcart-active': !shoppingCartExpanded.value,
			'expanded': shoppingCartExpanded.value
		};
	});

	const deliveryDateStrings = computed(() => {
		return shoppingCartStore.orderLinesPerWeek ? Array.from(shoppingCartStore.orderLinesPerWeek.keys()) : []
	});

	const deliveryDatesAmount = computed(() => deliveryDateStrings.value.length);

	const deliveryDates = computed(() => {
		// Map the order lines grouped by delivery date keys to an array of delivery dates.
		return deliveryDateStrings.value.map(key => {
			const [week, year] = key.split('-').map(Number);
			return {week, year};
		}).sort((a: DeliveryDate, b: DeliveryDate) => (a.year === b.year) ? (a.week - b.week) : (a.year - b.year));
	});

	const isCreatePoEnabled = computed(() => {
		return deliveryDatesAmount.value > 0 &&
			orderLines.value?.every(orderLine => !orderLine.notExistingVariety && orderLine.amount > 0);
	});

	// Methods
	const isRoleMasterOrSalesRep = hasRoleMasterOrSalesRep();

	const openPurchaseOrderModal = () => createPoModalOpen.value = true;

	const closePurchaseOrderModal = () => createPoModalOpen.value = false;

	const openChangeDeliveryAddressModal = (givenDeliveryAddresses: DeliveryAddress[]) => {
		deliveryAddresses.value = givenDeliveryAddresses;
		// Keep create-purchase-order-modal mounted (stacked underneath, same pattern as the stop-confirmation
		// modal in CreatePurchaseOrderModal.vue): toggling createPoModalOpen here would unmount/remount it via
		// its v-if, discarding its in-memory availabilityCheck state (shortages, progress) and forcing a full,
		// redundant re-check on close even though the delivery address doesn't affect product availability.
		changeDeliveryAddressModalOpen.value = true;
	};

	const closeChangeDeliveryAddressModal = () => {
		changeDeliveryAddressModalOpen.value = false;
	};

	const openCreatePurchaseOrderSuccessModal = (givenPoName: string) => {
		poName.value = givenPoName;
		createPoModalOpen.value = false;
		createPoSuccessModalOpen.value = true;
	};

	const closeCreatePurchaseOrderSuccessModal = () => createPoSuccessModalOpen.value = false;

	const toggleShoppingCart = () => {
		shoppingCartExpanded.value = !shoppingCartExpanded.value;

		if (!shoppingCartExpanded.value) {
			createOrderStore.reset();
		}
	}

	const closeShoppingCart = () => {
		const htmlElement = document.querySelector('html');

		setTimeout(() => {
			if (htmlElement) {
				htmlElement.classList.remove('scrollLock');
			}

			showContent.value = false;
		}, 900);

		shoppingCartToggle.value = false;
		toggleShoppingCart();
	};

	const toggle = async (checkAlternativePFs = false) => {
		// User can only open the cart, if there are 1 or more draft po's.
		if (!shoppingCartExpanded.value && deliveryDatesAmount.value === 0) {
			return;
		}

		if (shoppingCartExpanded.value) {
			closeShoppingCart();
			return;
		}

		document.querySelector('html')?.classList.add('scrollLock');

		toggleShoppingCart();

		await updateAvailabilityFromIndex();

		if (checkAlternativePFs) {
			const itemsWithAlternatives = shoppingCartStorage.value.orderLines.filter(orderLine => orderLine.alternativeForms && orderLine.alternativeForms.length > 0);
			if (itemsWithAlternatives.length > 0) {
				const groupMap = Map.groupBy(itemsWithAlternatives, line => line.groupId);
				const groups = Array.from(groupMap.values()).map(lines => {
					// Dedupe alternative forms by formId across all lines in the group.
					const alternativeFormsById = new Map(lines.flatMap(line => line.alternativeForms).map(form => [form.formId, form]));

					return {
						groupId: lines[0].groupId,
						groupName: lines[0].groupName,
						formName: lines[0].formName,
						alternativeForms: Array.from(alternativeFormsById.values()),
					};
				});
				modalStore.openAlternativePFModal(groups);
			}
		}

		shoppingCartStore.setInitialActiveWeek();

		showContent.value = true;
	};

	const applyAlternativePFSelection = async (selectedForm: AlternativeForm) => {
		if (!currentAlternativePFGroup.value) return;

		const groupId = currentAlternativePFGroup.value.groupId;

		shoppingCartStorage.value.orderLines = shoppingCartStorage.value.orderLines.map(orderLine => {
			// Only remap lines that are actually unavailable; lines with stock for their own week/form must stay untouched,
			// otherwise switching forms can flip a previously available line into an unavailable one (and vice versa).
			if (orderLine.groupId !== groupId || orderLine.amountAvailable !== 0) return orderLine;

			return {
				...orderLine,
				formId: selectedForm.formId,
				formName: selectedForm.shortFormName,
				packingUnit: selectedForm.packingUnit,
				allowOverbooking: selectedForm.allowOverbooking,
				alternativeForms: [],
				amount: roundUpAmount(orderLine.amount, selectedForm.packingUnit),
				notExistingVariety: false,
			};
		});

		await updateAvailabilityFromIndex();

		modalStore.goToNextAlternativePFGroup();
	};

	const setExtraHeaderTop = (shoppingCartHeader: string, myDummenHeader: string = '114px') => {
		const htmlElement = document.querySelector('html');

		if (htmlElement) {
			htmlElement.style.setProperty('--header-extra-top-for-shoppingcart', shoppingCartHeader);
			htmlElement.style.setProperty('--header-height', myDummenHeader);
		}
	};

	const showOrdersWithAvailabilityIssues = ref(false);

	const toggleOrdersWithAvailabilityIssues = (value: boolean) => {
		showOrdersWithAvailabilityIssues.value = value;
    if (value && window.plausible) {
      window.plausible('filteronunavailableproducts');
    }
	};

	watch(shoppingCartToggle, (doToggle) => {
		if (doToggle) {
			// Shopping cart is triggered by something from outside the component (such as the excel import and copy order),
			// so we check for the alternative PF's.
			toggle(true);
		}
	});

	watch(selectedCustomerId, (newCustomerId, oldCustomerId) => {
		if (isRoleMasterOrSalesRep && newCustomerId && newCustomerId !== oldCustomerId) {
			setExtraHeaderTop('50', '134px');
		} else {
			setExtraHeaderTop('30');
		}
	}, {immediate: true});

	onBeforeMount(() => {
		shoppingCartToggle.value = false;

		if (isRoleMasterOrSalesRep) {
			getRelatedCustomersForMasterOrSalesRep(relatedCustomersStore);
		}
	});

	onMounted(() => {
		initCustomerLoggedInIntervals();
	});
</script>

